// Inicializa la formalizacion una sola vez, aplica a inputs actuales
// y observa nuevos nodos para cubrir formularios renderizados dinamicamente.
export function startNumberInputFormalization() {
	if (hasStarted) {
		return
	}

	hasStarted = true
	installUnnormalizedValueProperty()

	const applyToCurrentInputs = () => {
		document
			.querySelectorAll<HTMLInputElement>('input[type="number"]:not(.skip-number-formalization)')
			.forEach((input) => formalizeNumberInput(input))
	}

	applyToCurrentInputs()

	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			mutation.addedNodes.forEach((node) => {
				if (!(node instanceof Element)) {
					return
				}

				if (node.matches('input[type="number"]:not(.skip-number-formalization)')) {
					formalizeNumberInput(node as HTMLInputElement)
				}

				node
					.querySelectorAll<HTMLInputElement>('input[type="number"]:not(.skip-number-formalization)')
					.forEach((input) => formalizeNumberInput(input))
			})
		})
	})

	observer.observe(document.documentElement, { childList: true, subtree: true })
}

let hasStarted = false
let hasInstalledUnnormalizedValueProperty = false
const normalizedSubmitForms = new WeakSet<HTMLFormElement>()

declare global {
	interface HTMLInputElement {
		readonly unnormalizedValue: string
	}
}

function installUnnormalizedValueProperty() {
	if (hasInstalledUnnormalizedValueProperty) {
		return
	}

	hasInstalledUnnormalizedValueProperty = true

	// Exponemos un valor normalizado para enviar al backend
	// sin perder el formato visual del input para el usuario.
	Object.defineProperty(HTMLInputElement.prototype, "unnormalizedValue", {
		configurable: true,
		enumerable: false,
		get: function (this: HTMLInputElement): string {
			if (this.dataset.numberFormalized === "true") {
				return normalizeValueForSubmit(this.value)
			}

			return this.value
		},
	})
}

function formalizeNumberInput(input: HTMLInputElement) {
	if (input.dataset.numberFormalized === "true") {
		return
	}

	// Conservamos restricciones originales del input[type=number]
	// para validarlas manualmente al enviar.
	input.dataset.numberFormalized = "true"
	input.dataset.originalMin = normalizeNumericAttributeString(input.getAttribute("min"))
	input.dataset.originalMax = normalizeNumericAttributeString(input.getAttribute("max"))
	input.dataset.originalStep = normalizeNumericAttributeString(input.getAttribute("step"))

	// Se cambia a texto para permitir separadores de miles y coma decimal.
	input.type = "text"
	input.pattern = "([0-9\\s]+(,[0-9]+)?|,[0-9]+)"
	input.inputMode = "numeric"

	input.addEventListener("input", thousandSeparatorEventListener)
	input.addEventListener("change", () => {
		input.setCustomValidity("")
		normalizeFormattedInputValue(input)
	})
	input.addEventListener("blur", () => {
		input.setCustomValidity("")
		normalizeFormattedInputValue(input)
	})

	if (!input.readOnly && !input.disabled) {
		normalizeFormattedInputValue(input)
	}

	const form = input.form
	if (!form) {
		return
	}

	bindNormalizedSubmitToForm(form)
}

function bindNormalizedSubmitToForm(form: HTMLFormElement) {
	if (normalizedSubmitForms.has(form)) {
		return
	}

	normalizedSubmitForms.add(form)

	// formdata asegura que el payload enviado use formato normalizado,
	// incluso si el valor visible contiene miles/coma local.
	form.addEventListener("formdata", (event: FormDataEvent) => {
		const normalizedInputs = Array.from(
			form.querySelectorAll<HTMLInputElement>('input[data-number-formalized="true"]')
		).filter((input) => input.name)

		normalizedInputs.forEach((input) => {
			event.formData.set(input.name, normalizeValueForSubmit(input.value))
		})
	})

	// submit replica validaciones de min/max/step del input numerico original.
	form.addEventListener("submit", (event: SubmitEvent) => {
		const normalizedInputs = Array.from(
			form.querySelectorAll<HTMLInputElement>('input[data-number-formalized="true"]')
		).filter((input) => input.name)

		if (normalizedInputs.length === 0) {
			return
		}

		for (const input of normalizedInputs) {
			const normalizedValue = normalizeValueForSubmit(input.value)
			if (!validateOriginalNumberConstraints(input, normalizedValue)) {
				event.preventDefault()
				input.reportValidity()
				return
			}
		}
	})
}

function normalizeValueForSubmit(value: string): string {
	// Convierte "1 234,56" -> "1234.56" para parseo consistente en backend.
	return value.replace(/\s+/g, "").replaceAll(",", ".")
}

function normalizeNumericAttributeString(value: string | null): string {
	if (!value) {
		return ""
	}

	const trimmedValue = value.trim().replace(",", ".")
	if (!/[eE]/.test(trimmedValue)) {
		return trimmedValue
	}

	// Tambien soporta min/max/step expresados en notacion cientifica.
	const scientificMatch = trimmedValue.match(/^([+-]?\d+(?:\.\d+)?)[eE]([+-]?\d+)$/)
	if (!scientificMatch) {
		return trimmedValue
	}

	const [, mantissaRaw, exponentRaw] = scientificMatch
	const exponent = Number(exponentRaw)
	if (!Number.isInteger(exponent)) {
		return trimmedValue
	}

	const sign = mantissaRaw.startsWith("-") ? "-" : ""
	const mantissa = mantissaRaw.replace(/^[-+]/, "")
	const [integerPart, fractionalPart = ""] = mantissa.split(".")
	const digits = `${integerPart}${fractionalPart}`
	const decimalIndex = integerPart.length + exponent

	let decimalValue: string
	if (decimalIndex <= 0) {
		decimalValue = `0.${"0".repeat(Math.abs(decimalIndex))}${digits}`
	} else if (decimalIndex >= digits.length) {
		decimalValue = `${digits}${"0".repeat(decimalIndex - digits.length)}`
	} else {
		decimalValue = `${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`
	}

	const normalizedDecimal = decimalValue
		.replace(/^0+(\d)/, "$1")
		.replace(/^\./, "0.")

	return `${sign}${normalizedDecimal}`
}

function validateOriginalNumberConstraints(input: HTMLInputElement, normalizedValue: string): boolean {
	input.setCustomValidity("")

	if (!normalizedValue) {
		return true
	}

	const value = Number(normalizedValue)
	if (!Number.isFinite(value)) {
		input.setCustomValidity(getValidationMessage("invalidNumber"))
		return false
	}

	const minAttr = input.dataset.originalMin
	if (minAttr) {
		const min = Number(minAttr)
		if (Number.isFinite(min) && value < min) {
			input.setCustomValidity(getValidationMessage("min", { value: minAttr }))
			return false
		}
	}

	const maxAttr = input.dataset.originalMax
	if (maxAttr) {
		const max = Number(maxAttr)
		if (Number.isFinite(max) && value > max) {
			input.setCustomValidity(getValidationMessage("max", { value: maxAttr }))
			return false
		}
	}

	// Misma semantica que step nativo, con tolerancia para errores de punto flotante.
	const stepAttr = input.dataset.originalStep
	if (stepAttr && stepAttr !== "any") {
		const step = Number(stepAttr)
		if (Number.isFinite(step) && step > 0) {
			const minBase = minAttr && Number.isFinite(Number(minAttr)) ? Number(minAttr) : 0
			const stepFactor = (value - minBase) / step
			const epsilon = 1e-9
			if (Math.abs(stepFactor - Math.round(stepFactor)) > epsilon) {
				input.setCustomValidity(getValidationMessage("step", { value: stepAttr }))
				return false
			}
		}
	}

	return true
}

function thousandSeparatorEventListener(event: Event) {
	const target = event.target as HTMLInputElement
	normalizeFormattedInputValue(target)
}

function normalizeFormattedInputValue(input: HTMLInputElement) {
	// Mantiene formato local durante la escritura: miles con espacio y coma decimal.
	input.value = formatWithThousandSeparator(input.value)

	const permittedDecimals = getStepPermittedDecimals(input)
	if (permittedDecimals === null) {
		return
	}

	// Si step limita decimales, redondea para mantener consistencia visual y de envio.
	const unnormalizedValue = input.unnormalizedValue
	const decimalPart = unnormalizedValue.split(".")[1] || ""
	if (decimalPart.length <= permittedDecimals) {
		return
	}

	const numericValue = Number(unnormalizedValue)
	if (!Number.isFinite(numericValue)) {
		return
	}

	const roundedValue = stripTrailingDecimalZeros(numericValue.toFixed(permittedDecimals))
	input.value = formatWithThousandSeparator(roundedValue)
}

function stripTrailingDecimalZeros(value: string): string {
	return value
		.replace(/(\.\d*?[1-9])0+$/, "$1")
		.replace(/\.0+$/, "")
}

function getStepPermittedDecimals(input: HTMLInputElement): number | null {
	const stepAttr = input.dataset.originalStep
	if (!stepAttr || stepAttr === "any") {
		return null
	}

	const normalizedStep = stepAttr.replace(",", ".")
	const decimalPart = normalizedStep.split(".")[1]
	if (!decimalPart) {
		return 0
	}

	return decimalPart.replace(/0+$/, "").length
}

type NumberValidationMessageKey = "invalidNumber" | "min" | "max" | "step"

function getValidationMessage(key: NumberValidationMessageKey, values: { value?: string } = {}): string {
	const bodyDataset = document.body?.dataset

	// Permite internacionalizar mensajes via data-* sin acoplar este modulo a i18n.
	const templates: Record<NumberValidationMessageKey, string> = {
		invalidNumber:
			bodyDataset?.numberInputInvalidNumberMessage ||
			"Ingrese un número válido",
		min:
			bodyDataset?.numberInputMinMessage ||
			"El valor debe ser mayor o igual a %{value}",
		max:
			bodyDataset?.numberInputMaxMessage ||
			"El valor debe ser menor o igual a %{value}",
		step:
			bodyDataset?.numberInputStepMessage ||
			"El valor debe respetar un incremento de %{value}",
	}

	return templates[key].replaceAll("%{value}", values.value || "")
}

export function formatWithThousandSeparator(value: string): string {
	// El usuario puede pegar texto con punto decimal o espacios:
	// primero se limpia y se normaliza a formato local.
	const sanitizedValue = value.replace(/\s/g, "").replaceAll(".", ",")
	const normalizedValue = sanitizedValue.startsWith(",")
		? `0${sanitizedValue}`
		: sanitizedValue

	if (!/^\d+(,\d*)?$/.test(normalizedValue)) {
		return value
	}

	const [integerPart, decimalPart] = normalizedValue.split(",")
	const formattedInteger = Number(integerPart)
		.toLocaleString("es-PY")
		.replaceAll(".", " ")

	if (decimalPart === undefined) {
		return formattedInteger
	}

	return `${formattedInteger},${decimalPart}`
}